import { useState } from 'react';
import { peopleAPI } from '../../../../services/api';

export interface Person {
  id: string;
  employeeId?: string;
  employee_id?: string;
  name: string;
  email?: string;
  role?: string;
  hourlyRate?: number;
  hourly_rate?: number;
  partnerName?: string;
  projectId?: string;
  projectName?: string;
}

interface LoadPeopleOptions {
  showTimesheetView?: boolean;
  projects?: Array<{ id: string; name: string }>;
}

interface ApiResult {
  success: boolean;
  data?: Person[];
  error?: string;
}

export const usePeople = () => {
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(false);
  const [partnerEmployees, setPartnerEmployees] = useState<Person[]>([]);
  const [partnerEmployeesLoading, setPartnerEmployeesLoading] = useState(false);

  const loadPeople = async (projectId: string | null | undefined, options: LoadPeopleOptions = {}): Promise<void> => {
    const { showTimesheetView, projects } = options;

    setLoading(true);
    try {
      if (showTimesheetView) {
        if (projectId) {
          const result: ApiResult = await peopleAPI.getProjectPeople(projectId);
          if (result.success) {
            setPeople(result.data ?? []);
          } else {
            console.error('Error loading people:', result.error);
            setPeople([]);
          }
        } else if (projects) {
          const allPeople: Person[] = [];
          for (const project of projects) {
            const result: ApiResult = await peopleAPI.getProjectPeople(project.id);
            if (result.success) {
              const projectPeople = (result.data ?? []).map(person => ({
                ...person,
                projectId: project.id,
                projectName: project.name
              }));
              allPeople.push(...projectPeople);
            }
          }
          // Remove duplicates based on employeeId
          const uniquePeople = allPeople.reduce<Person[]>((acc, person) => {
            const existing = acc.find(p => p.employeeId === person.employeeId);
            if (!existing) {
              acc.push(person);
            }
            return acc;
          }, []);
          setPeople(uniquePeople);
        }
      } else {
        // Original behavior for project view
        if (!projectId) return;
        const result: ApiResult = await peopleAPI.getProjectPeople(projectId);
        if (result.success) {
          setPeople(result.data ?? []);
        } else {
          console.error('Error loading people:', result.error);
          setPeople([]);
        }
      }
    } catch (err: unknown) {
      console.error('Error loading people:', err);
      setPeople([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPartnerEmployees = async (): Promise<void> => {
    setPartnerEmployeesLoading(true);
    try {
      const result: ApiResult = await peopleAPI.getPartnerEmployees();
      if (result.success) {
        setPartnerEmployees(result.data ?? []);
      } else {
        console.error('Error loading partner employees:', result.error);
        setPartnerEmployees([]);
      }
    } catch (err: unknown) {
      console.error('Error loading partner employees:', err);
      setPartnerEmployees([]);
    } finally {
      setPartnerEmployeesLoading(false);
    }
  };

  const addPerson = async (projectId: string, personData: Partial<Person>): Promise<ApiResult> => {
    const result: ApiResult = await peopleAPI.addProjectPerson(projectId, personData);
    if (result.success) {
      await loadPeople(projectId);
    }
    return result;
  };

  const updatePerson = async (projectId: string, personId: string, updates: Partial<Person>): Promise<ApiResult> => {
    const result: ApiResult = await peopleAPI.updateProjectPerson(projectId, personId, updates);
    if (result.success) {
      await loadPeople(projectId);
    }
    return result;
  };

  const removePerson = async (projectId: string, personId: string): Promise<ApiResult> => {
    const result: ApiResult = await peopleAPI.removeProjectPerson(projectId, personId);
    if (result.success) {
      await loadPeople(projectId);
    }
    return result;
  };

  return {
    people,
    loading,
    partnerEmployees,
    partnerEmployeesLoading,
    loadPeople,
    loadPartnerEmployees,
    addPerson,
    updatePerson,
    removePerson
  };
};
